<script>
import PhoenixForm from '@phoenix/mixins/components/form/PhoenixForm';
import { useForm } from 'vee-validate';
import { defineAsyncComponent } from 'vue';

export default {
  components: {
    ChinesePIPL: defineAsyncComponent(() => import('@maison/components/form/fields/ChinesePIPL')),
    KoreanGDPR: defineAsyncComponent(() => import('@maison/components/form/KoreanGDPR')),
  },

  mixins: [PhoenixForm],

  props: {
    isContained: {
      type: Boolean,
      default: true,
    },
    customSubmitButtonClasses: {
      type: String,
      default: null,
    },
    isSubmitOnLeft: {
      type: Boolean,
      default: false,
    },
    naked: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    const { setValues, validate } = useForm();
    return { setValues, validate };
  },

  computed: {
    submitButtonClasses() {
      return this.customSubmitButtonClasses || 'btn btn--default btn--negative';
    },

    submitButtonContainerClasses() {
      return [
        'mt-base',
        {
          'flex justify-around': !this.isSubmitOnLeft,
        },
      ];
    },

    termsLinkClasses() {
      return ['mt-xs', { 'flex flex-grid--center text-center': !this.isSubmitOnLeft }];
    },

    shouldDisplayAfterTerms() {
      return !!this.$slots['after-terms'];
    },
  },
};
</script>

<template>
  <form ref="form" class="relative" @submit.prevent="submit">
    <div v-if="isLoading" class="flex flex-grid--center">
      <div class="loader-overlay loader-overlay--light">
        <div class="loader loader--dark loader--lg">
          <div class="loader__inner"></div>
        </div>
      </div>
    </div>

    <div :class="[{ container: isContained }]">
      <slot :should-display-newsletter="!shouldDisplayKoreanGDPR" />
    </div>

    <template v-if="!naked">
      <korean-g-d-p-r
        v-if="shouldDisplayKoreanGDPR"
        :should-handle-newsletter="shouldHandleKoreanGDPRNewsletter"
        :initial-values="formValues"
        :form-type="koreanFormType"
        @change="onKoreanGDPRChange"
        @consent-values-loaded="onConsentValuesLoaded"
      />

      <chinese-p-i-p-l
        v-if="shouldDisplayPIPLConsentCheckboxes"
        :value="chinesePiplConsents"
        :custom-inner-class="customConsentInnerClass"
        :custom-outer-class="customConsentOuterClass"
        :form-type="formType"
        @on-chinese-pipl-consent-change="onChinesePiplConsentChange"
      />

      <div v-if="!showErrorsInToast && mainErrors" class="alert alert--error mt-base">
        {{ mainErrors }}
      </div>

      <div v-if="shouldShowFormActions">
        <div :class="submitButtonContainerClasses">
          <slot name="form-actions" :is-disabled="isDisabled" />

          <button
            v-if="shouldDisplayPreviousButton"
            class="btn btn--default btn--negative"
            type="button"
            @click.prevent="goToPreviousStep"
          >
            <span class="btn__text">{{ previousStepText }}</span>
          </button>

          <button
            v-if="shouldDisplayCancelButton"
            ref="cancelButton"
            :disabled="isDisabled"
            :class="cancelClass"
            data-cy="form-cancel-btn"
            @click.prevent="onCancelCallback"
          >
            <span class="btn__text">{{ cancelText }}</span>
          </button>

          <button
            v-if="shouldDisplaySubmitButton"
            ref="submitButton"
            :disabled="isDisabled"
            :class="submitButtonClasses"
            type="submit"
            data-cy="form-submit-btn"
          >
            <span class="btn__text">{{ submitText }}</span>
          </button>
        </div>

        <div v-if="shouldDisplayTermsLink" :class="termsLinkClasses">
          <p v-html="termsLink" />
        </div>

        <div v-if="shouldDisplayAfterTerms" :class="termsLinkClasses">
          <slot name="after-terms" />
        </div>

        <p v-if="shouldDisplayPIPLStatement" data-cy="pipl-statement" class="pt-base" v-html="piplStatement" />
      </div>
    </template>
  </form>
</template>
